import React, { useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';

import CustomSearchDropdown from './CustomSearchDropDwon';
import { importConfigAPI } from '../services/importConfigAPI';
import { useImport } from '../context/ImportContext';

const CompanyLinkInline = ({ configId, itensLayout }) => {
  const toast = useRef(null);
  const { setLoading: setContextLoading } = useImport();

  const [companies, setCompanies] = useState([]);

  const [loading, setLoading] = useState(false);
  const [searchEmpresa, setSearchEmpresa] = useState(null);
  const [dropdownClearSignal, setDropdownClearSignal] = useState(0);


  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [newLayoutName, setNewLayoutName] = useState('');

  const loadCompanies = async () => {
    if (!configId) return;

    setLoading(true);
    try {
      const response = await importConfigAPI.listCompaniesByConfig(configId);
      if (response?.success) {
        setCompanies(response.data || []);
      } else {
        setCompanies([]);
      }
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar empresas vinculadas' });
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!itensLayout) return;

    // Se não houver configId, não existe vínculo possível.
    if (!configId) {
      itensLayout(0);
      setCompanies([]);
      return;
    }

    // quando trocar configId, reseta o total enquanto carrega
    itensLayout(0);
    setCompanies([]);
    loadCompanies();
  }, [configId]);

  // Mantém o componente de busca sempre limpo quando o vínculo mudar
  // (assim, ao vincular ou desvincular, o input não fica com valor antigo).


  useEffect(() => {
    if (!itensLayout) return;
    // Mantém o total sempre sincronizado com a lista carregada.
    itensLayout(companies?.length ?? 0);
  }, [companies]);


  const createCompanyLink = async () => {
    if (!configId || !searchEmpresa) return;

    setContextLoading(true);
    try {
await importConfigAPI.linkCompanyLayout({
        config_id: configId,
        ...searchEmpresa,
        // compatibilidade: antes o vínculo esperava `id`; agora deve vir `cod_emp`/`emp_cli`
        cod_emp: searchEmpresa.cod_emp ?? searchEmpresa.emp_cli ?? searchEmpresa.id,
      });
      toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Empresa vinculada' });

      // limpa input do dropdown e o estado interno
      setSearchEmpresa(null);
      setDropdownClearSignal((v) => v + 1);

      await loadCompanies();
    } catch (e) {
      const apiMessage =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        e?.response?.data?.errors?.[0] ||
        null;

      const isBusinessWarning = Boolean(apiMessage);

      toast.current?.show({
        severity: isBusinessWarning ? 'warn' : 'error',
        summary: isBusinessWarning ? 'Atenção' : 'Erro',
        detail: apiMessage || 'Falha ao vincular empresa',
      });
    } finally {
      setContextLoading(false);
    }
  };

  const deleteCompanyLink = async (id) => {
    if (!configId || !id) return;

    setContextLoading(true);
    try {
      await importConfigAPI.unlinkCompanyLayout(id);
      toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Empresa desvinculada' });
      await loadCompanies();
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao desvincular empresa' });
    } finally {
      setContextLoading(false);
    }
  };

  const [unlinkConfirmVisible, setUnlinkConfirmVisible] = useState(false);
  const [unlinkTargetId, setUnlinkTargetId] = useState(null);

  const confirmUnlinkCompany = (id) => {
    setUnlinkTargetId(id);
    setUnlinkConfirmVisible(true);
  };

  const onAcceptUnlink = () => {
    if (!unlinkTargetId) {
      setUnlinkConfirmVisible(false);
      return;
    }

    deleteCompanyLink(unlinkTargetId);
    setUnlinkConfirmVisible(false);
    setUnlinkTargetId(null);
  };

  const onRejectUnlink = () => {
    setUnlinkConfirmVisible(false);
    setUnlinkTargetId(null);
  };


  const openEditModal = (rowData) => {
    setSelectedRow(rowData);
    setNewLayoutName(rowData.fantasy_name || '');
    setEditModalVisible(true);
  };

  const saveLayoutName = async () => {
    if (!newLayoutName.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Aviso', detail: 'Nome do layout não pode ser vazio' });
      return;
    }
    if (!selectedRow) return;

    setContextLoading(true);
    try {
      await importConfigAPI.updateFantasyName({
        cod_emp: selectedRow.emp_cli ?? selectedRow.cod_emp ?? selectedRow.id,
        base: selectedRow.base,
        fantasy_name: newLayoutName
      });
      toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Nome do layout atualizado' });
      setEditModalVisible(false);
      await loadCompanies();
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao atualizar nome do layout' });
    } finally {
      setContextLoading(false);
    }
  };

  const actionsBody = (rowData) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        tooltip="Editar Nome para o Cliente"
        rounded
        outlined
        severity="info"
        size="small"
        text
        onClick={() => openEditModal(rowData)}
      />
      <Button
        icon="pi pi-trash"
        tooltip="Remover Vínculo"
        rounded
        outlined
        severity="danger"
        size="small"
        text
onClick={() => confirmUnlinkCompany(rowData.emp_cli ?? rowData.cod_emp ?? rowData.id)}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <Dialog
        header="Confirmação de Exclusão"
        visible={unlinkConfirmVisible}
        onHide={onRejectUnlink}
        modal
        style={{ width: '420px' }}
        footer={
          <div className="flex gap-2">
            <Button label="Cancelar" icon="pi pi-times" outlined onClick={onRejectUnlink} />
            <Button
              label="Sim, desvincular"
              icon="pi pi-exclamation-triangle"
              severity="danger"
              onClick={onAcceptUnlink}
            />
          </div>
        }
      >
        <div className="flex align-items-center gap-3">
          <i className="pi pi-exclamation-triangle text-red-500" style={{ fontSize: '1.2rem' }} />
          <span>Você tem certeza que deseja Desvincular a empresa ?</span>
        </div>
      </Dialog>

      <Dialog
        header="Editar Nome para o cliente"
        visible={editModalVisible}
        onHide={() => setEditModalVisible(false)}
        modal
        style={{ width: '400px' }}
        footer={
          <div className="flex gap-2">
            <Button label="Cancelar" icon="pi pi-times" outlined onClick={() => setEditModalVisible(false)} />
            <Button label="Salvar" icon="pi pi-check" onClick={saveLayoutName} />
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <label htmlFor="layoutName">Nome para o Cliente</label>
          <InputText
            id="layoutName"
            value={newLayoutName}
            onChange={(e) => setNewLayoutName(e.target.value)}
            placeholder="Digite o novo nome para o cliente"
          />
        </div>
      </Dialog>

      <div className="mt-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold text-gray-700">Empresa - Layout</span>
        </div>

        <div className="flex gap-2">
          <div className="flex-grow">
            <CustomSearchDropdown
              placeholder="Buscar empresas"
              onSelect={(item) => setSearchEmpresa(item)}
              clearSignal={dropdownClearSignal}
            />
          </div>
          <Button
            tooltip="Vincular Empresa"
            disabled={!searchEmpresa}
            icon="pi pi-plus"
            severity="success"
            onClick={createCompanyLink}
            loading={loading}
            className="h-[40px]"
          />
        </div>

        <div className="mt-3">
          <DataTable
            value={companies}
            paginator
            rows={10}
            loading={loading}
            emptyMessage="Sem dados para apresentar"
            size="small"
          >
            <Column field="fantasy_name" header="Nome Para o Cliente" />
            <Column field="layout_id" header="Layout" />
            <Column header="Ações" body={actionsBody} style={{ minWidth: '70px' }} />
          </DataTable>
        </div>
      </div>
    </>
  );
};

export default CompanyLinkInline;

